// ============================================================
// /src/ui/screens/ProWycenaScreen.tsx (MAIN CONTAINER - ORKIESTRACJA)
// ============================================================

import React, { useState, useCallback } from "react";
import { useProWycena } from "@/hooks/useProWycena";
import { UploadTrigger } from "../components/UploadTrigger";
import { TrustIndicator } from "../components/TrustIndicator";
import { BusinessAlert } from "../components/BusinessAlert";
import { ReferenceSelector } from "../components/ReferenceSelector";
import ProfitBar from "../components/ProfitBar";

export const ProWycenaScreen: React.FC = () => {
  const { flow, uploadImage, selectReference, reset } = useProWycena();
  const [recovered, setRecovered] = useState(0);
  const [lastChange, setLastChange] = useState(0);

  const handleRecover = useCallback((amount: number) => {
    setRecovered(prev => {
      setLastChange(amount);
      return prev + amount;
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white font-mono pt-[80px] pb-8 px-4">
      <ProfitBar totalRecovered={recovered} lastChange={lastChange} />
      
      <div className="max-w-md mx-auto space-y-6">
        {/* STAGE: IDLE / UPLOAD */}
        {(flow.stage === "IDLE" || flow.stage === "ANALYZING") && (
          <div className="space-y-4">
            <h1 className="text-2xl font-black uppercase tracking-tighter">
              ProWycena AI
            </h1>
            <p className="text-[#666] text-sm">
              Zdjęcie pomaga rozpoznać kontekst. Metraż potwierdza wykonawca.
            </p>
            
            <UploadTrigger 
              onFileSelect={uploadImage}
              disabled={flow.stage === "ANALYZING"}
            />
            
            {flow.stage === "ANALYZING" && (
              <div className="text-[#00e676] text-sm animate-pulse">
                Analiza wizualna w toku...
              </div>
            )}
          </div>
        )}

        {/* STAGE: REFERENCE SELECT (FALLBACK) */}
        {flow.stage === "REFERENCE_SELECT" && (
          <div className="space-y-4">
            <div className="text-[#ff3b3b] font-black uppercase text-sm">
              🔴 FALLBACK · Niska ufność AI
            </div>
            <p className="text-[#666] text-sm">
              Wybierz znany element na zdjęciu, aby przeskalować pomiar.
            </p>
            <ReferenceSelector onSelect={selectReference} />
          </div>
        )}

        {/* STAGE: CALCULATING */}
        {flow.stage === "CALCULATING" && (
          <div className="text-center py-12">
            <div className="text-[#00e676] text-lg animate-pulse">
              Kalkulacja kosztów...
            </div>
          </div>
        )}

        {/* STAGE: RESULT */}
        {flow.stage === "RESULT" && (
          <div className="space-y-6">
            {/* Trust Layer */}
            <div className="flex justify-between items-center border-b border-[#333] pb-4">
              <span className="text-[#666] text-xs uppercase">Wynik analizy</span>
              <TrustIndicator confidence={flow.estimate.meta.aiConfidence} />
            </div>

            {/* Spatial Data */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#1a1a1a] p-4 border border-[#333]">
                <div className="text-[#666] text-xs uppercase mb-1">Powierzchnia</div>
                <div className="text-2xl font-black text-[#00e676]">
                  {flow.estimate.spatial?.flatSurfaceM2.toFixed(1)} m²
                </div>
              </div>
              <div className="bg-[#1a1a1a] p-4 border border-[#333]">
                <div className="text-[#666] text-xs uppercase mb-1">Krawędzie</div>
                <div className="text-2xl font-black text-white">
                  {flow.estimate.spatial?.edgesMB.toFixed(1)} mb
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="bg-[#1a1a1a] border border-[#333] p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#666]">Materiały</span>
                <span>{flow.estimate.costs.materials.toFixed(0)} zł</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#666]">Robocizna</span>
                <span>{flow.estimate.costs.labor.toFixed(0)} zł</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#ff3b3b]">Bufor ryzyka</span>
                <span>+{flow.estimate.costs.riskBuffer.toFixed(0)} zł</span>
              </div>
              <div className="border-t border-[#333] pt-2 flex justify-between text-xl font-black">
                <span className="text-[#00e676]">RAZEM</span>
                <span className="text-[#00e676]">{flow.estimate.costs.total.toFixed(0)} zł</span>
              </div>
            </div>

            {/* Business Alerts */}
            <BusinessAlert 
              rules={flow.estimate.rules} 
              onRecover={handleRecover}
            />

            {/* Reset */}
            <button
              type="button"
              onClick={reset}
              className="w-full min-h-[44px] border border-[#333] text-[#666] 
                       hover:text-white hover:border-white
                       focus:ring-2 focus:ring-[#00e676] focus:outline-none
                       text-xs uppercase"
            >
              Nowa wycena
            </button>
          </div>
        )}

        {/* STAGE: ERROR */}
        {flow.stage === "ERROR" && (
          <div 
            role="alert"
            className="bg-[#2a0f0f] border-l-4 border-[#ff3b3b] p-4"
          >
            <div className="text-[#ff3b3b] font-black uppercase mb-2">
              ⚠️ Błąd systemu
            </div>
            <div className="text-white text-sm mb-4">
              {flow.message}
            </div>
            <button
              type="button"
              onClick={reset}
              className="w-full min-h-[44px] bg-[#ff3b3b] text-white font-black uppercase"
            >
              Spróbuj ponownie
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
